import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { Reveal, SectionHead } from "@/components/layout/Shell";
import { formatDate, isUrl, mediaUrl } from "@/lib/api";

const viewAll = (to, label, testId) => (
  <Link to={to} className="text-link" data-testid={testId}>{label} <ArrowRight size={15} /></Link>
);

export const CommitteeGrid = ({ committee = [] }) => (
  <section className="section">
    <div className="container">
      <SectionHead eyebrow="People who lead" title={<>Meet the <em className="gold-text">committee</em></>} link={viewAll("/leadership", "View all leadership", "committee-view-all")} />
      <div className="people-grid">
        {committee.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <article className="person-card" data-testid={`committee-card-${i}`}>
              <img src={p.image} alt={p.name} />
              <div>
                <span>{p.position}</span>
                <h3>{p.name}</h3>
                <p>{p.bio}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const EventsPreview = ({ events = [] }) => (
  <section className="section tinted">
    <div className="container">
      <SectionHead eyebrow="On the calendar" title={<>Make time for <em className="gold-text">what moves you</em></>} link={viewAll("/events", "All events", "events-view-all")} />
      <div className="events-grid">
        {events.map((e, i) => {
          const d = new Date(e.date);
          return (
            <Reveal key={e.id} delay={i * 0.1}>
              <article className="event-card" data-testid={`event-card-${i}`}>
                <img src={e.image} alt={e.title} />
                <div className="event-date">
                  {String(d.getDate()).padStart(2, "0")}
                  <small>{d.toLocaleDateString("en-IN", { month: "short" })}</small>
                </div>
                <div className="event-body">
                  <div className="event-meta">
                    <span>{e.category}</span>
                    <b>{formatDate(e.date)}</b>
                  </div>
                  <h3>{e.title}</h3>
                  <p>{e.description}</p>
                  <Link to="/events" className="text-link" data-testid={`event-details-${i}`}>
                    Event details <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const HomeLower = ({ albums = [], news = [] }) => (
  <section className="home-lower">
    <div className="container lower-grid">
      <Reveal>
        <article className="mini-gallery" data-testid="home-gallery">
          <div className="section-head">
            <div><span className="eyebrow">In the field</span><h2>Gallery</h2></div>
            {viewAll("/gallery", "View all", "gallery-view-all")}
          </div>
          <div className="gallery-grid">
            {albums.map((album, i) => (
              <Link to={`/gallery/${album.id}`} key={album.id} data-testid={`gallery-card-${i}`}>
                <img src={mediaUrl(album.cover)} alt={album.title} />
                <span>{album.title}</span>
              </Link>
            ))}
          </div>
        </article>
      </Reveal>
      <Reveal delay={0.1}>
        <article className="mini-news" data-testid="home-news">
          <div className="section-head">
            <div><span className="eyebrow">Latest updates</span><h2>News & notices</h2></div>
            {viewAll("/news", "View all", "news-view-all")}
          </div>
          {news.map((item, i) => (
            <Link to="/news" className="news-row" key={item.id} data-testid={`news-row-${i}`}>
              <span>{item.date}</span>
              <strong>{item.title}</strong>
              <ArrowRight size={14} />
            </Link>
          ))}
        </article>
      </Reveal>
      <Reveal delay={0.2}>
        <article className="member-cta" data-testid="home-membership-cta">
          <Users size={30} />
          <h2>Be a part of <span className="gold-text">DCEA</span></h2>
          <p>Join a growing network of civil engineering professionals and contribute to a stronger tomorrow.</p>
          <Link to="/membership" className="button button-gold" data-testid="home-membership-button">
            Become a member <ArrowRight size={15} />
          </Link>
        </article>
      </Reveal>
    </div>
  </section>
);

export const SponsorStrip = ({ sponsors = [] }) => {
  const loop = sponsors.length ? [...sponsors, ...sponsors, ...sponsors] : [];
  return (
    <section className="sponsor-strip" data-testid="sponsor-marquee-section">
      <div className="container sponsor-in">
        <div>
          <span className="eyebrow">In good company</span>
          <h2>Our sponsors & partners</h2>
        </div>
        <Link to="/sponsors" className="button button-dark" data-testid="sponsors-view-all">
          View partners <ArrowRight size={15} />
        </Link>
      </div>
      <div className="marquee" data-testid="sponsor-marquee">
        <div className="marquee-track" style={{ animationDuration: `${Math.max(18, loop.length * 4)}s` }}>
          {loop.map((s, i) => (
            <a href={s.website || "#"} target="_blank" rel="noreferrer" className="marquee-item" key={`${s.id}-${i}`} data-testid={i < sponsors.length ? `sponsor-${s.id}` : undefined}>
              {isUrl(s.logo) ? <img src={mediaUrl(s.logo)} alt={s.name} /> : <strong>{s.logo}</strong>}
              <span>{s.name}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
